import { SetStateAction, createContext, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Step, SubmitAnswersRequest } from "../interfaces";
import { DealerData } from "../interfaces/IDealerData";
import SubmitAnswersService from "../services/SubmitAnswersService";
import TransactionService from "../services/TransactionService";
import { useAuth } from "../hooks/useAuth";
import DealerService from "../services/DealerService";
import { BrandType } from "../types";
import { BrandType as Brand } from "@bus/models";
import { capitalizeBrand } from "../helpers/capitalizeBrand";
import { mapFromBFFModel } from "../mappings/mapFromBFFModel";

interface IStepGroupPath {
    id: number;
    path: string;
    title: string;
    stepEnum?: Step | null;
}

export interface IStepGroup {
    id: number;
    paths: IStepGroupPath[];
    title: string;
}

export interface IStep {
    id: number;
    path: IStepGroupPath;
    title: string;
}

export type WizardContextType = {
    currentStep: IStep;
    steps: IStepGroup[];
    submitAnswersRequest: SubmitAnswersRequest;
    selectedDealerTemp: string | undefined;
    isEdit: boolean;
    isSkipSteps: boolean;
    isLoading: boolean;
    defaultDealer: DealerData | undefined;
    dealersList: DealerData[];
    firstSearch: boolean;

    goToQuestionsStep: (step: number, isEdit: boolean) => void;
    goToDealerStep: (isEdit: boolean) => void;
    goToPersonalDataStep: (isEdit: boolean) => void;
    goToPreviousStep: () => void;
    getTotalSteps: () => number;

    setIsEdit: React.Dispatch<boolean>;
    setSubmitAnswersRequest: React.Dispatch<React.SetStateAction<SubmitAnswersRequest>>;
    setSelectedDealerTemp: React.Dispatch<string | undefined>;
    setIsSkipSteps: React.Dispatch<boolean>;
    setIsLoading: React.Dispatch<boolean>;
    setDefaultDealer: React.Dispatch<DealerData | undefined>;
    setDealersList: React.Dispatch<DealerData[]>;
    setFirstSearch: React.Dispatch<SetStateAction<boolean>>;
};

export const WizardContext = createContext<WizardContextType | undefined>(undefined);

interface WizardProviderProps {
    children: React.ReactNode;
}

const WizardProvider: React.FC<WizardProviderProps> = ({ children }) => {
    const navigate = useNavigate();
    const { transactionId, dealerCompanyNumber, setLoading, handleDataTemplateChange } = useAuth();
    const { t } = useTranslation("matchmakerwizard");
    const brand = (process.env.REACT_APP_WEBSITE_BRAND as BrandType).toUpperCase();

    const getStep1Title = () => {
        switch (brand.toLowerCase()) {
            case Brand.VWN:
                return t("step1.path.1VWN");
            default:
                return t("step1.path.1", { brand: capitalizeBrand(brand as Brand) });
        }
    }

    const getStep1SecondTitle = () => {
        switch (brand.toLowerCase()) {
            case Brand.VWN:
                return t("step1.path.2VWN");
            case Brand.VW:
                return t("step1.path.2VW");
            default:
                return t("step1.path.2", { brand: capitalizeBrand(brand as Brand) });
        }
    }

    const getStep2Title = () => {
        switch (brand.toLowerCase()) {
            case Brand.VWN:
                return t("step2.path.1VWN");
            case Brand.VW:
                return t("step2.path.1VW");
            case Brand.AUDI:
                return t("step2.path.1Audi");
            default:
                return t("step2.path.1", { brand: capitalizeBrand(brand as Brand) });
        }
    }

    const [steps] = useState<IStepGroup[]>([
        {
            id: 1,
            paths: [
                {
                    id: 1,
                    path: "/match-maker/question-page1",
                    title: getStep1Title(),
                    stepEnum: Step.MM1,
                },
                {
                    id: 2,
                    path: "/match-maker/question-page2",
                    title: getStep1SecondTitle(),
                    stepEnum: Step.MM2,
                },
                {
                    id: 3,
                    path: "/match-maker/question-page3",
                    title: t("step1.path.3"),
                    stepEnum: Step.MM3,
                },
                {
                    id: 4,
                    path: "/match-maker/question-page4",
                    title: t("step1.path.4"),
                    stepEnum: Step.MM4,
                },
            ],
            title: t("step1.title"),
        },
        {
            id: 2,
            paths: [
                {
                    id: 1,
                    path: "/dealer-selection",
                    title: getStep2Title(),
                    stepEnum: Step.DEALER_SELECTION,
                },
                {
                    id: 2,
                    path: "/personal-information",
                    title: t("step2.path.2"),
                    stepEnum: Step.CONTACT_DETAILS,
                },
            ],
            title: t("step2.title"),
        },
        {
            id: 3,
            paths: [
                {
                    id: 1,
                    path: "/summary",
                    title: t("step3.path.1"),
                    stepEnum: Step.FINAL_CONFIRMATION,
                },
            ],
            title: t("step3.title"),
        },
        {
            id: 4,
            paths: [
                {
                    id: 1,
                    path: "/confirmation",
                    title: "",
                    stepEnum: Step.FINAL_CONFIRMATION_PAGE,
                },
            ],
            title: t("step4.title"),
        },
    ]);
    const [currentStep, setCurrentStep] = useState<IStep>({
        id: steps[0].id,
        path: steps[0].paths[0],
        title: steps[0].title,
    });
    const goToQuestionsStep = (step: number, isEdit: boolean) => {
        setIsEdit(isEdit);
        const newStep = {
            id: steps[0].id,
            path: steps[0].paths[step - 1],
            title: steps[0].title,
        };
        setCurrentStep(newStep);
        handleDataTemplateChange(newStep.path.stepEnum ?? Step.MM1, "pagechange", undefined, submitAnswersRequest);
        return;
    };
    const goToDealerStep = useCallback(
        (isEdit: boolean) => {
            setIsEdit(isEdit);
            const newStep = {
                id: steps[1].id,
                path: steps[1].paths[0],
                title: steps[1].title,
            };
            setCurrentStep(newStep);
            handleDataTemplateChange(
                newStep.path.stepEnum ?? Step.DEALER_SELECTION,
                "pagechange",
                undefined,
                submitAnswersRequest
            );
            return;
        },
        [steps]
    );
    const goToPersonalDataStep = useCallback(
        (isEdit: boolean) => {
            setIsEdit(isEdit);
            const newStep = {
                id: steps[1].id,
                path: steps[1].paths[1],
                title: steps[1].title,
            };
            setCurrentStep(newStep);
            handleDataTemplateChange(
                newStep.path.stepEnum ?? Step.CONTACT_DETAILS,
                "pagechange",
                undefined,
                submitAnswersRequest
            );
            return;
        },
        [steps]
    );
    const goToSummaryStep = useCallback(() => {
        const newStep = {
            id: steps[2].id,
            path: steps[2].paths[0],
            title: steps[2].title,
        };
        setCurrentStep(newStep);
        handleDataTemplateChange(
            newStep.path.stepEnum ?? Step.FINAL_CONFIRMATION,
            "pagechange",
            undefined,
            submitAnswersRequest
        );
        return;
    }, [steps]);
    const goToPreviousStep = () => {
        const selectedStep = steps.find((step: IStepGroup) => step.id === currentStep.id);

        if (!selectedStep) return;

        if (selectedStep?.paths.findIndex((path: IStepGroupPath) => path === currentStep.path) === 0) {
            const previousStep = steps.find((step: IStepGroup) => step.id === currentStep.id - 1);
            if (!previousStep) return;
            const newStep = {
                id: previousStep.id,
                path: previousStep.paths[previousStep.paths.length - 1],
                title: previousStep.title,
            };
            setCurrentStep(newStep);
            handleDataTemplateChange(newStep.path.stepEnum ?? Step.MM1, "pagechange", undefined, submitAnswersRequest);
            return;
        }

        const previousPath = selectedStep?.paths.find((path: IStepGroupPath) => path === currentStep.path);
        if (!previousPath) return;

        const previousIndex = selectedStep?.paths.findIndex((path: IStepGroupPath) => path === previousPath) - 1;
        if (previousIndex === -1) return;
        const newStep = {
            id: currentStep.id,
            path: selectedStep.paths[previousIndex],
            title: currentStep.title,
        };
        setCurrentStep(newStep);

        handleDataTemplateChange(newStep.path.stepEnum ?? Step.MM1, "pagechange", undefined, submitAnswersRequest);
    };
    const getTotalSteps = () => {
        return steps.length;
    };

    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [firstSearch, setFirstSearch] = useState<boolean>(true);
    const [isSkipSteps, setIsSkipSteps] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [defaultDealer, setDefaultDealer] = useState<DealerData>();
    const [dealersList, setDealersList] = useState<DealerData[]>([]);
    const [selectedDealerTemp, setSelectedDealerTemp] = useState<string | undefined>(undefined);
    const [submitAnswersRequest, setSubmitAnswersRequest] = useState<SubmitAnswersRequest>({
        isEditMode: false,
        currentStep: Step.START,
    });

    const goToNextStep = useCallback(() => {
        const selectedStep = steps.find((step: IStepGroup) => step.id === currentStep.id);

        if (!selectedStep) return;

        if (
            selectedStep?.paths.findIndex((path: IStepGroupPath) => path === currentStep.path) ===
            selectedStep?.paths.length - 1
        ) {
            const nextStep = steps.find((step: IStepGroup) => step.id === currentStep.id + 1);
            if (!nextStep) return;
            const newStep = {
                id: nextStep.id,
                path: nextStep.paths[0],
                title: nextStep.title,
            };
            setCurrentStep(newStep);
            handleDataTemplateChange(newStep.path.stepEnum ?? Step.MM1, "pagechange", undefined, submitAnswersRequest);
            return;
        }

        const nextPath = selectedStep?.paths.find((path: IStepGroupPath) => path === currentStep.path);
        if (!nextPath) return;

        const nextIndex = selectedStep?.paths.findIndex((path: IStepGroupPath) => path === nextPath) + 1;
        if (nextIndex === -1) return;
        const newStep = {
            id: currentStep.id,
            path: selectedStep.paths[nextIndex],
            title: currentStep.title,
        };
        setCurrentStep(newStep);

        handleDataTemplateChange(newStep.path.stepEnum ?? Step.MM1, "pagechange", undefined, submitAnswersRequest);
    }, [currentStep.id, currentStep.path, steps, submitAnswersRequest]);

    useEffect(() => {
        async function getDefaultDealer() {
            if (dealerCompanyNumber) {
                const dealerService = new DealerService();
                const { data, status } = await dealerService.getDealersById(dealerCompanyNumber);
                if (status !== 200) {
                }
                setDefaultDealer(data);
            }
        }
        getDefaultDealer();
    }, [dealerCompanyNumber]);
    
    useEffect(() => {
        async function loadSavedTransaction() {
            if (transactionId) {
                try {
                    // Try to load saved transaction data
                    const transactionService = new TransactionService();
                    const { data: savedTransaction, status } = await transactionService.getTransaction(transactionId);
                    
                    if (status === 200 && savedTransaction) {
                        // Map the saved BFF data back to SubmitAnswersRequest format
                        const restoredRequest = mapFromBFFModel(savedTransaction);
                        
                        if (restoredRequest) {
                            console.log("Restored transaction:", restoredRequest);
                            
                            // Set the restored request
                            setSubmitAnswersRequest(restoredRequest);
                            
                            // Navigate to the saved step
                            const savedStep = restoredRequest.currentStep;
                            
                            // Find the corresponding step in the steps array
                            for (const stepGroup of steps) {
                                const pathIndex = stepGroup.paths.findIndex(p => p.stepEnum === savedStep);
                                if (pathIndex !== -1) {
                                    const newStep = {
                                        id: stepGroup.id,
                                        path: stepGroup.paths[pathIndex],
                                        title: stepGroup.title,
                                    };
                                    setCurrentStep(newStep);
                                    handleDataTemplateChange(
                                        newStep.path.stepEnum ?? Step.MM1,
                                        "Initialized",
                                        undefined,
                                        restoredRequest
                                    );
                                    break;
                                }
                            }
                            
                            setLoading(false);
                            return;
                        }
                    }
                } catch (error) {
                    console.error("Failed to load saved transaction:", error);
                }
                
                // If loading fails or no saved data, initialize with new request
                setSubmitAnswersRequest({
                    ...submitAnswersRequest,
                    transactionId: transactionId,
                    volkswagenGroupPartnerIds: dealerCompanyNumber ? [dealerCompanyNumber] : [],
                });
            }
        }
        if (transactionId) loadSavedTransaction();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transactionId]);

    useEffect(() => {
        async function updateSubmitAnswers() {
            if (submitAnswersRequest) {
                setIsLoading(true);
                const answersServices = new SubmitAnswersService();
                const { status } = await answersServices.submitAnswers(submitAnswersRequest);
                setIsLoading(false);
                if (status !== 200) {
                    //TODO:Validar erros
                    navigate("error-page");
                    return;
                }
                if (submitAnswersRequest.currentStep !== Step.START) {
                    if (isEdit) {
                        goToSummaryStep();
                        setIsEdit(false);
                        return;
                    }
                    if (isSkipSteps) {
                        goToDealerStep(false);
                        setIsSkipSteps(false);
                        return;
                    }
                    goToNextStep();
                } else {
                    setLoading(false);
                }
            }
        }
        if (transactionId && submitAnswersRequest.transactionId) updateSubmitAnswers();
    }, [submitAnswersRequest]);

    useEffect(() => {
        navigate(currentStep.path.path);
    }, [currentStep, navigate]);

    return (
        <WizardContext.Provider
            value={{
                currentStep,
                steps,
                submitAnswersRequest,
                selectedDealerTemp,
                isEdit,
                isSkipSteps,
                isLoading,
                defaultDealer,
                dealersList,

                goToDealerStep,
                goToQuestionsStep,
                goToPersonalDataStep,
                goToPreviousStep,
                firstSearch,

                getTotalSteps,
                setSubmitAnswersRequest,
                setSelectedDealerTemp,
                setIsEdit,
                setIsSkipSteps,
                setIsLoading,
                setDefaultDealer,
                setDealersList,
                setFirstSearch,
            }}
        >
            {children}
        </WizardContext.Provider>
    );
};

export default WizardProvider;
